import { CHARACTER_ENGINE } from '../constants/simulationConstants';
import avatarEmotionConfig from '../config/avatarEmotionConfig';
import {
  AVATURN_ANIMATED_FACE_MORPH_NAMES,
  EMOTION_BLENDSHAPES,
  EMOTION_BLENDSHAPE_PATTERN,
  EMOTION_MESH_NAME,
  SPEECH_SAFE_MOUTH_PRESERVE_NAMES
} from '../constants/emotionBlendshapes';

export const NEUTRAL_EMOTION_PAYLOAD = { name: 'neutral', intensity: 1 };

/**
 * Convai getEmotionResponse() returns a protobuf wrapper; emotion lives in `.array`:
 * [0] name, [1] intensity.
 */
export function parseConvaiEmotionResponse(emotionResponse) {
  if (!emotionResponse) return null;

  const array = emotionResponse.array;
  if (Array.isArray(array) && array[0]) {
    return {
      name: String(array[0]),
      intensity: Number(array[1]) || 1
    };
  }

  return null;
}

export function resetConvaiEmotionState({ setEmotion }) {
  setEmotion(NEUTRAL_EMOTION_PAYLOAD);
}

export function isAvaturnAnimatedFaceMorph(
  name,
  meshName = EMOTION_MESH_NAME.head
) {
  return (
    meshName === EMOTION_MESH_NAME.head &&
    AVATURN_ANIMATED_FACE_MORPH_NAMES.includes(name)
  );
}

export function stripAvaturnAnimatedBlendshapes(
  blendshapes,
  meshName = EMOTION_MESH_NAME.head
) {
  if (meshName !== EMOTION_MESH_NAME.head) return { ...(blendshapes ?? {}) };

  return Object.fromEntries(
    Object.entries(blendshapes ?? {}).filter(
      ([name]) => !isAvaturnAnimatedFaceMorph(name, meshName)
    )
  );
}

export function getEmotionPayloadKey(emotionPayload) {
  if (!emotionPayload || typeof emotionPayload !== 'object') return 'neutral:1';

  const name = String(
    emotionPayload.name ?? emotionPayload.emotion ?? 'neutral'
  ).toLowerCase();
  const intensity = Number(emotionPayload.intensity) || 1;

  return `${name}:${intensity}`;
}

export function isNeutralEmotionPayload(emotionPayload) {
  if (!emotionPayload || typeof emotionPayload !== 'object') return true;

  const name = String(
    emotionPayload.name ?? emotionPayload.emotion ?? 'neutral'
  ).toLowerCase();

  return name === 'neutral';
}

export function isMouthBlendshape(name) {
  return EMOTION_BLENDSHAPE_PATTERN.lipsyncMouth.test(name);
}

export function isSpeechSafeMouthBlendshape(name) {
  return EMOTION_BLENDSHAPE_PATTERN.speechSafeMouth.test(name);
}

export function isCoreEmotionEngine(engineType) {
  return engineType === CHARACTER_ENGINE.NUDGYT_CORE;
}

/** Agent clients (Core + Coaching) share LLM emotion payloads and use Core presets. */
export function resolveEmotionEngineType({
  characterEngineType,
  isAgentClient
}) {
  return isAgentClient ? CHARACTER_ENGINE.NUDGYT_CORE : characterEngineType;
}

export function getEmotionTransitionAlpha({ delta, speed }) {
  const dt = Number.isFinite(delta) && delta > 0 ? delta : 1 / 60;
  return 1 - Math.exp(-speed * dt);
}

export function lerpBlendshapeMap(current, target, alpha) {
  const keys = new Set([
    ...Object.keys(current ?? {}),
    ...Object.keys(target ?? {})
  ]);
  const result = {};

  keys.forEach((name) => {
    const from = current?.[name] ?? 0;
    const to = target?.[name] ?? 0;
    const value = from + (to - from) * alpha;

    if (value > 1e-4) {
      result[name] = value;
    }
  });

  return result;
}

/** Drop lip-shaping morphs lipsync owns so smoothed emotion cannot fight visemes. */
export function stripLipsyncConflictBlendshapes(blendshapes) {
  return Object.fromEntries(
    Object.entries(blendshapes ?? {}).filter(
      ([name]) => !isMouthBlendshape(name) || isSpeechSafeMouthBlendshape(name)
    )
  );
}

export function stripMouthBlendshapes(blendshapes) {
  return Object.fromEntries(
    Object.entries(blendshapes ?? {}).filter(
      ([name]) => !isMouthBlendshape(name)
    )
  );
}

export function pickSpeechSafeMouthBlendshapes(blendshapes) {
  return Object.fromEntries(
    Object.entries(blendshapes ?? {}).filter(([name]) =>
      isSpeechSafeMouthBlendshape(name)
    )
  );
}

export function partitionEmotionBlendshapes(blendshapes) {
  const upperFace = {};
  const mouth = {};

  Object.entries(blendshapes ?? {}).forEach(([name, value]) => {
    if (isMouthBlendshape(name)) mouth[name] = value;
    else upperFace[name] = value;
  });

  return { upperFace, mouth };
}

export function getEmotionBlendShapeRampSpeed({
  isTalking = false,
  engineType
} = {}) {
  const { blendShapeRamp } = avatarEmotionConfig.transitionSpeed;

  if (isTalking) return blendShapeRamp.whileTalking;

  return isCoreEmotionEngine(engineType)
    ? blendShapeRamp.core
    : blendShapeRamp.convai;
}

export function getEmotionMeshLerpSpeed({ isTalking = false } = {}) {
  const { meshLerp } = avatarEmotionConfig.transitionSpeed;

  return isTalking ? meshLerp.whileTalking : meshLerp.idle;
}

function getEmotionTable() {
  return EMOTION_BLENDSHAPES;
}

/** Expand a preset into per-mesh targets (supports `meshes` or legacy `object`). */
export function expandEmotionPreset(preset) {
  if (!preset) return [];

  if (preset.meshes) {
    return Object.entries(preset.meshes).map(([object, blendshapes]) => ({
      intensity: preset.intensity,
      object,
      blendshapes
    }));
  }

  if (preset.object && preset.blendshapes) {
    return [preset];
  }

  return [];
}

export function resolveEmotionIntensityPreset(
  emotionPayload,
  { engineType } = {}
) {
  if (!emotionPayload || typeof emotionPayload !== 'object') return null;

  const rawName = emotionPayload.name ?? emotionPayload.emotion ?? 'neutral';
  const emotionName = String(rawName).toLowerCase();

  if (emotionName === 'neutral') return null;

  const intensity = Number(emotionPayload.intensity) || 1;
  const clampedIntensity = Math.min(3, Math.max(1, Math.round(intensity)));
  const table = getEmotionTable();

  return (
    table[emotionName]?.find(
      (preset) => preset.intensity === clampedIntensity
    ) ?? null
  );
}

export function resolveEmotionPresets(emotionPayload, { engineType } = {}) {
  const preset = resolveEmotionIntensityPreset(emotionPayload, { engineType });
  return expandEmotionPreset(preset);
}

export function resolveEmotionPreset(emotionPayload, options = {}) {
  return resolveEmotionPresets(emotionPayload, options)[0] ?? null;
}

export function getPresetMeshName(preset) {
  return preset?.object ?? EMOTION_MESH_NAME.head;
}

export function getActiveEmotionBlendshapesByMesh(
  presets,
  { isTalking = false, engineType } = {}
) {
  const byMesh = {};

  (presets ?? []).forEach((preset) => {
    byMesh[getPresetMeshName(preset)] = getActiveEmotionBlendshapes(preset, {
      isTalking,
      engineType
    });
  });

  return byMesh;
}

export function smoothEmotionBlendshapesByMesh({
  previousByMesh = {},
  targetByMesh = {},
  isTalking = false,
  justStoppedTalking = false,
  rampAlpha
}) {
  const nextByMesh = { ...previousByMesh };

  Object.entries(targetByMesh).forEach(([meshName, targetBlendshapes]) => {
    if (isTalking) {
      const { upperFace: targetUpperFace, mouth: targetMouth } =
        partitionEmotionBlendshapes(targetBlendshapes);
      const speechSafeMouth = pickSpeechSafeMouthBlendshapes(targetMouth);
      const previousMesh = previousByMesh[meshName] ?? {};
      const upperOnlySmoothed = lerpBlendshapeMap(
        stripMouthBlendshapes(previousMesh),
        targetUpperFace,
        rampAlpha
      );

      nextByMesh[meshName] = {
        ...upperOnlySmoothed,
        ...speechSafeMouth
      };
      return;
    }

    if (justStoppedTalking) {
      nextByMesh[meshName] = {
        ...(previousByMesh[meshName] ?? {}),
        ...targetBlendshapes
      };
      return;
    }

    nextByMesh[meshName] = lerpBlendshapeMap(
      previousByMesh[meshName] ?? {},
      targetBlendshapes,
      rampAlpha
    );
  });

  return nextByMesh;
}

export function scaleEmotionBlendshapes(blendshapes, scale) {
  if (!blendshapes || scale === 1) return blendshapes;

  return Object.fromEntries(
    Object.entries(blendshapes).map(([name, value]) => [name, value * scale])
  );
}

export function getCoreEmotionConfidenceScale(emotionPayload) {
  const confidence = Number(emotionPayload?.confidence);
  if (!Number.isFinite(confidence)) return 1;
  return Math.min(1, Math.max(0.35, confidence));
}

export function getActiveEmotionBlendshapes(
  preset,
  { isTalking = false, engineType } = {}
) {
  if (!preset) return {};

  const { coreTalkingScale } = avatarEmotionConfig;
  let entries = Object.entries(preset.blendshapes);

  if (isTalking) {
    entries = entries.filter(
      ([name]) => !isMouthBlendshape(name) || isSpeechSafeMouthBlendshape(name)
    );

    if (isCoreEmotionEngine(engineType)) {
      entries = entries.map(([name, value]) => [
        name,
        value *
          (name === 'mouthSmile'
            ? 1
            : isSpeechSafeMouthBlendshape(name)
              ? coreTalkingScale.mouth
              : coreTalkingScale.upperFace)
      ]);
    }
  }

  return Object.fromEntries(entries);
}

let emotionOwnedNamesByMeshCache = null;

export function getEmotionOwnedBlendshapeNamesByMesh() {
  if (emotionOwnedNamesByMeshCache) return emotionOwnedNamesByMeshCache;

  const byMesh = {};

  [EMOTION_BLENDSHAPES].forEach((table) => {
    Object.values(table).forEach((presets) => {
      presets.forEach((preset) => {
        expandEmotionPreset(preset).forEach(
          ({ object: meshName, blendshapes }) => {
            if (!byMesh[meshName]) byMesh[meshName] = new Set();

            Object.keys(blendshapes).forEach((name) =>
              byMesh[meshName].add(name)
            );
          }
        );
      });
    });
  });

  emotionOwnedNamesByMeshCache = Object.fromEntries(
    Object.entries(byMesh).map(([meshName, names]) => [meshName, [...names]])
  );

  return emotionOwnedNamesByMeshCache;
}

/** Blendshape names any preset can set — used to release morphs on a Neutral payload. */
export function getEmotionOwnedBlendshapeNames() {
  const ownedByMesh = getEmotionOwnedBlendshapeNamesByMesh();

  return [...new Set(Object.values(ownedByMesh).flat())];
}

export { SPEECH_SAFE_MOUTH_PRESERVE_NAMES };
